import { useCallback, useContext, useEffect, useRef } from 'react'
import { toast } from 'react-toastify'
import { CoreContext } from 'context/CoreContext'
import useI18n from './useI18n'
import {
    dismissWebPushPrompt,
    isWebPushSupported,
    markWebPushDenied,
    requestAndEnableWebPush,
    shouldPromptForWebPush,
    subscribeToForegroundMessages,
    syncWebPushToken,
} from 'services/webNotifications'

export default function useWebNotifications({ onForegroundMessage } = {}) {
    const { modal, setModal, user } = useContext(CoreContext)
    const { t } = useI18n()
    const promptOpenedRef = useRef(false)

    const handleEnable = useCallback(async () => {
        const result = await requestAndEnableWebPush()

        if (result?.ok) {
            toast.success(t('web_notifications_enabled') || 'Notifications enabled successfully.')
            setModal(null)
            return true
        }

        if (result?.reason === 'unsupported') {
            toast.info(t('web_notifications_not_supported') || 'This browser does not support web notifications.')
            setModal(null)
            return false
        }

        if (result?.permission === 'denied') {
            markWebPushDenied()
            toast.info(t('web_notifications_denied') || 'Notifications are blocked in your browser settings.')
            setModal(null)
        }

        return false
    }, [setModal, t])

    const handleDismiss = useCallback(() => {
        dismissWebPushPrompt()
        setModal(null)
    }, [setModal])

    useEffect(() => {
        let isMounted = true

        const init = async () => {
            if (!user?.id) return

            const supported = await isWebPushSupported()
            if (!isMounted || !supported) return

            if (Notification.permission === 'granted') {
                await syncWebPushToken()
                return
            }

            if (Notification.permission === 'denied') {
                markWebPushDenied()
                return
            }

            if (modal || promptOpenedRef.current || !shouldPromptForWebPush()) {
                return
            }

            promptOpenedRef.current = true
            setModal({
                type: 'web-notification',
                onEnable: handleEnable,
                onDismiss: handleDismiss,
            })
        }

        init()

        return () => {
            isMounted = false
        }
    }, [handleDismiss, handleEnable, modal, setModal, user?.id])

    useEffect(() => {
        if (modal?.type !== 'web-notification') {
            promptOpenedRef.current = false
        }
    }, [modal])

    useEffect(() => {
        let unsubscribe = () => {}

        const bindForegroundMessages = async () => {
            if (!user?.id) return

            unsubscribe = await subscribeToForegroundMessages((message, payload) => {
                toast.info(
                    <>
                        <strong>{message.title}</strong>
                        {message.body ? <div>{message.body}</div> : null}
                    </>
                )
                onForegroundMessage?.(payload)
            })
        }

        bindForegroundMessages()

        return () => {
            unsubscribe()
        }
    }, [onForegroundMessage, user?.id])
}
